import { useTranslation } from "next-i18next"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useRouter } from "next/router"
import PropTypes from "prop-types"
import Coach from "@/components/core/Coach/Coach"
import MiniCart from "@/components/Shared/MiniCart/v2/MiniCart"
import useViewport from "@/hooks/useViewport"
import { selectCartState } from "@/store/features/cartSlice"
import { getAriaLabel, getCartCount } from "@/utils/cart"
import { getProjectionPartListData } from "@/utils/ProjectionPartlist.js"
import printIcon from "@/public/scout_icon/icons/printIcon.svg"
import shareIcon from "@/public/scout_icon/icons/shareIcon.svg"
import PrintAndShare from "@/components/PartsViewer/v2/PrintShareWindow/index"
import PrintShare from "@/components/PartsViewer/v2/SelectedFileView/PrintShare"
import PrintWindow from "@/components/PartsViewer/v2/PrintWindow"
import { getUpdatedReplaceFile } from "@/components/PartsViewer/v2/utils"
import { isCanadaSite } from "@/utils/helper"
import { getTranslations } from "@/components/PartsViewer/v2/localization"

const FileSelection = props => {
  const {
    files,
    isActive,
    setSelected,
    setPopupModal,
    title,
    sku,
    skuImage,
    getFileTitle,
    setParts,
    panelState,
    initialIndex,
    setPartsHeight,
    productId,
    setShowPrint,
    siteName,
  } = props || {}
  const [visibleCoach, setVisibleCoach] = useState(false)
  const device = useViewport()
  const divRef = useRef(null)
  const tabRefs = useRef([])
  const { t } = useTranslation("common")
  const { coachMarkText } = getTranslations(t)
  const cartCount = getCartCount()
  const ariaLabel = getAriaLabel(t, cartCount)
  const { cartUrl } = useSelector(selectCartState)
  const [isOverlapping, setIsOverlapping] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const [share, setShare] = useState(false)
  const [showPrintWindow, setShowPrintWindow] = useState(false)
  const kohlerCanadaFlag = isCanadaSite(siteName)
  const shareBtn = useRef()
  const marginTab = kohlerCanadaFlag ? "mr-110" : "mr-150"
  const featuresDtl = {
    displayClass: "d-inline",
    floating:
      device?.isSmallScreen || device?.isMediumScreen ? "" : "float-right",
    heightof: "h-80",
    skuDisplayClass:
      device?.isSmallScreen || device?.isMediumScreen ? "" : "d-inline",
  }
  const [features, setFeatures] = useState(featuresDtl)
  const router = useRouter()

  const redirect = () => {
    router.push(`${cartUrl}`)
  }

  const eventHandler = {
    store: {
      cartCount,
      ariaLabel,
      cartUrl,
    },
    callbacks: {
      onClick: () => {
        redirect()
      },
    },
  }

  const scrollVisible = index => {
    const addCoachContainer = document.querySelector(".add-coach")
    const offSetValue =
      tabRefs?.current[index]?.getBoundingClientRect()?.left -
      addCoachContainer?.getBoundingClientRect()?.left
    if (tabRefs?.current[index]) {
      tabRefs?.current[index]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      })
      if (!device?.isSmallScreen) {
        const timer = setTimeout(
          () => (addCoachContainer.scrollLeft += offSetValue),
          100
        )
        return () => clearTimeout(timer)
      }
    }
  }
  const adjustSrcollForView = () => {
    const addCoachContainer = document.querySelector(".add-coach")
    const activeTabContent = document.querySelector(".activeTab")
    const addCoachContainerRect = addCoachContainer?.getBoundingClientRect()
    const activeTabContentRect = activeTabContent?.getBoundingClientRect()
    const offset = activeTabContentRect?.left - addCoachContainerRect?.left
    addCoachContainer.scrollLeft += offset
  }

  useEffect(() => {
    window.addEventListener("resize", adjustSrcollForView)
    return () => {
      window.removeEventListener("resize", adjustSrcollForView)
    }
  }, [])

  useEffect(() => {
    scrollVisible(initialIndex)
  }, [files, initialIndex])
  useEffect(() => {
    setPartsHeight(features?.heightof)
    const timer = setTimeout(() => setVisibleCoach(true), 3000)
    return () => clearTimeout(timer)
  }, [])

  const screenWidth =
    device.isLargeScreen || device.isExtraLargeScreen || device.isMediumScreen

  const handlerFileSelection = async (e, item, index) => {
    if (!item?.prices && item?.Parts?.length !== 0) {
      const listData = await getProjectionPartListData(item?.Parts)
      const prices = listData.pricing
      setParts(prevState => {
        const {
          partsInfo: { SVGFiles },
        } = prevState
        const updatePrice = SVGFiles?.filter(
          val => val.FileName === item.FileName
        )[0]
        updatePrice.replacementPartsInfo = listData.replacementPartsInfo
        updatePrice.defaultPartsList = updatePrice.Parts
        updatePrice.replacementParts = listData.replacementParts
        updatePrice.Parts = getUpdatedReplaceFile(updatePrice)
        updatePrice.prices = prices
        return { ...prevState }
      })
    }
    setSelected(item)
    scrollVisible(index)
    setPopupModal(false)
  }
  useEffect(() => {
    const checkOverlap = () => {
      if (device?.isLargeTabView) {
        let addCoachSpan = 0
        const childDiv = Array.from(
          document?.querySelector(".add-coach")?.children
        )
        childDiv.forEach(
          child => (addCoachSpan += child?.getBoundingClientRect()?.width)
        )
        if (
          document?.querySelector(".sku-title")?.getBoundingClientRect()
            ?.width +
            document?.querySelector(".sku")?.getBoundingClientRect()?.width +
            addCoachSpan +
            200 >
          document.querySelector(".section-title-file")?.getBoundingClientRect()
            ?.width
        ) {
          setIsOverlapping(true)
        } else {
          setIsOverlapping(false)
        }
      }
    }
    checkOverlap()
    window.addEventListener("resize", checkOverlap)
    return () => window.removeEventListener("resize", checkOverlap)
  }, [])

  useEffect(() => {
    if (isOverlapping) {
      setPartsHeight("h-115")
      setFeatures(prevfeatures => ({
        ...prevfeatures,
        displayClass: "d-inline",
        floating: "",
        heightof: "h-115",
        marginTb: "my-3",
        skuDisplayClass: "",
      }))
    } else {
      setFeatures(featuresDtl)
      setPartsHeight("h-80")
    }
  }, [isOverlapping])

  return (
    <div
      className={`check-modal d-flex flex-grow-1 ${features.heightof} ${
        showPopup ? "show-print-window" : ""
      }`}
    >
      <div className="flex-container modal_panel selection">
        <div className={`only-image image-container`}>
          <img className={`image-part`} src={skuImage} alt="skuimage" />
        </div>

        <div className={`section-title-file ${features.displayClass} w-100`}>
          <div className={`modal_header flex-item ${features.displayClass}`}>
            <div className={`sku-part ${features.skuDisplayClass}`}>
              <span className="sku-title">{title}</span>
              <span className="sku">{sku}</span>
            </div>
          </div>

          <div className={`flex-container header-content ${features.floating}`}>
            {files.length !== 1 && visibleCoach ? (
              <Coach
                text={coachMarkText}
                show={true}
                showHeaderCoach={true}
                panelState={panelState}
                isOverlapping={isOverlapping}
                displayKey="top"
              />
            ) : null}
            <div ref={divRef} className="add-coach">
              {files
                ?.filter(files => "FileName" in files)
                ?.map((item, index) => {
                  return (
                    <span
                      key={`${index + item?.FileName}`}
                      ref={el => (tabRefs.current[index] = el)}
                      className={
                        "file-tab user-select-none  header-icons header-name" +
                        (isActive(item) ? " activeTab" : "")
                      }
                      onClick={e => {
                        handlerFileSelection(e, item, index)
                      }}
                      onKeyDown={val => val}
                    >
                      {getFileTitle(item?.FileName)}
                    </span>
                  )
                })}
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${
          !(device.isMediumScreen || device.isSmallScreen)
            ? `${marginTab} print-action`
            : "mobile-print-section"
        }`}
      >
        {screenWidth ? (
          <div className="print-share-div d-flex">
            <div className="header-img d-flex">
              <button
                className="btn-share-print"
                onClick={() => {
                  setShowPopup(true)
                  setShare(false)
                  setShowPrintWindow(true)
                  setShowPrint(true)
                }}
                aria-label="Click here to print"
              >
                <img src={printIcon?.src} alt={"print"} />
              </button>

              <button
                aria-label="Click here to share"
                className="share-button"
                onClick={() => {
                  setShowPopup(true)
                  setShare(true)
                  setShowPrintWindow(false)
                  setShowPrint(true)
                }}
                ref={shareBtn}
              >
                <img src={shareIcon?.src} alt={"share"} />
              </button>
              {share ? (
                <PrintAndShare
                  showPopup={showPopup}
                  setShowPopup={setShowPopup}
                  contents={
                    <PrintShare
                      showShareModal={share}
                      setShare={setShare}
                      shareBtn={shareBtn}
                      productId={productId}
                    />
                  }
                />
              ) : null}
              {showPrintWindow ? (
                <PrintAndShare
                  showPopup={showPopup}
                  setShowPopup={setShowPopup}
                  contents={<PrintWindow files={files} productId={productId} />}
                />
              ) : null}
            </div>
          </div>
        ) : (
          ""
        )}
      </div>

      {!kohlerCanadaFlag && (
        <div className="parts-cart-wrapper">
          <MiniCart eventHandler={eventHandler} source={"from desk"} />
        </div>
      )}
    </div>
  )
}

FileSelection.propTypes = {
  files: PropTypes.array,
}

export default FileSelection
